import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, Crown, Medal, ArrowUpRight, TrendingUp, MapPin } from "lucide-react";

const API = `${BACKEND_URL}/api`;

// ISO 3166-1 alpha-2 → flag emoji (regional indicator symbols)
const flag = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  const base = 0x1F1E6 - 65;
  return String.fromCodePoint(...cc.toUpperCase().split("").map(c => c.charCodeAt(0) + base));
};

const fmtGBP = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Tiny sparkline (no external lib — pure SVG)
function Spark({ data = [], color = "#0055FF" }) {
  if (!data.length) return null;
  const w = 80, h = 24, pad = 2;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = pad + (i / Math.max(1, data.length - 1)) * (w - pad * 2);
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`${pts} ${w - pad},${h - pad} ${pad},${h - pad}`} fill={`${color}1a`} stroke="none" />
    </svg>
  );
}

function PodiumCard({ partner, accent, offset = "", icon: Icon, rankLabel }) {
  return (
    <div className={`relative flex flex-col min-w-0 ${offset}`} data-testid={`podium-rank-${partner.rank}`}>
      <div className={`${accent.bg} border ${accent.border} rounded-t-md flex flex-col items-center px-2.5 sm:px-4 pb-4 sm:pb-5 relative overflow-hidden`}>
        {/* Top banner — full-width sliver, never collides with content */}
        <div className={`w-full flex items-center justify-center py-1.5 text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.16em] sm:tracking-[0.18em] ${accent.chipBg} ${accent.chipText} border-b ${accent.divider}`}>
          {rankLabel}
        </div>

        {/* Subtle metallic highlight */}
        <div className="absolute inset-x-0 top-7 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
        <div className={`absolute -top-12 left-1/2 -translate-x-1/2 size-32 sm:size-40 rounded-full ${accent.glow} blur-3xl opacity-40 pointer-events-none`} />

        {/* Icon */}
        <div className={`relative flex items-center justify-center size-9 sm:size-11 rounded-sm ${accent.iconBg} ${accent.iconText} mt-4 sm:mt-5 mb-2 sm:mb-3 border ${accent.iconBorder}`}>
          <Icon className="size-4 sm:size-5" strokeWidth={1.6} />
        </div>

        <div className="text-xl sm:text-2xl mb-0.5 sm:mb-1 relative leading-none">{flag(partner.country)}</div>
        <div className={`font-semibold tracking-tight relative text-[13px] sm:text-base text-center break-words leading-tight ${accent.text}`}>{partner.alias}</div>
        {partner.city && (
          <div className={`text-[10px] sm:text-[11px] mt-1 inline-flex items-center gap-0.5 ${accent.subtext}`}>
            <MapPin className="size-2.5" /> {partner.city}
          </div>
        )}

        <div className={`mt-4 sm:mt-5 pt-3 sm:pt-4 border-t w-full text-center ${accent.divider}`}>
          <div className={`text-base sm:text-2xl font-semibold tabular-nums tracking-tight ${accent.text} break-all leading-none`}>
            {fmtGBP(partner.commission)}
          </div>
          <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] mt-1.5 ${accent.subtext}`}>
            comm · 30d
          </div>
        </div>
      </div>
      <div className={`h-7 sm:h-9 ${accent.base} flex items-center justify-center text-[11px] sm:text-sm font-mono font-semibold tracking-[0.18em] sm:tracking-[0.2em] ${accent.baseText} rounded-b-md border-t ${accent.baseBorder}`}>
        #{partner.rank}
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    axios.get(`${API}/public/affiliate-leaderboard?period=${period}&limit=10`)
      .then(r => setData(r.data))
      .catch(() => setData({ partners: [] }));
  }, [period]);

  const partners = data?.partners || [];
  const top3 = partners.slice(0, 3);
  const rest = partners.slice(3);

  // Reorder podium so rank #1 is centre (rank 2 left, rank 1 centre, rank 3 right)
  const podium = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="leaderboard-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_-10%,rgba(0,85,255,0.08),transparent_60%)] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24 relative">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#0055FF] font-semibold mb-4">
            <Trophy className="size-3.5" /> Partner Leaderboard
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-slate-900 max-w-3xl leading-[1.05]">
            The partners powering<br />
            <span className="bg-gradient-to-r from-[#0055FF] to-[#22D3A6] bg-clip-text text-transparent">financial growth</span>.
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl text-[15px] leading-relaxed">
            Top 10 referral partners ranked by commission earned in the last {period === "week" ? "7 days" : period === "all" ? "all time" : "30 days"}.
            Identities anonymised — performance speaks for itself.
          </p>

          {/* Period switcher */}
          <div className="mt-8 inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-soft" data-testid="period-switcher">
            {[
              { id: "week", label: "7 days" },
              { id: "month", label: "30 days" },
              { id: "all", label: "All time" },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${period === p.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Podium */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {!data && <div className="text-center text-sm text-slate-500">Loading…</div>}

        {data && partners.length === 0 && (
          <div className="text-center py-16 text-sm text-slate-500" data-testid="leaderboard-empty">
            No partners ranked yet — be the first.
          </div>
        )}

        {partners.length > 0 && (
          <>
            {/* Podium top-3 — items-start so the offset margins create the tier effect cleanly */}
            <div className="grid grid-cols-3 gap-2 sm:gap-5 items-start mb-12">
              {podium.map((p) => {
                const isWinner = p.rank === 1;
                const isSilver = p.rank === 2;
                const accent = isWinner
                  ? {
                      bg: "bg-slate-950",
                      border: "border-slate-800",
                      text: "text-white",
                      subtext: "text-white/55",
                      divider: "border-white/12",
                      iconBg: "bg-white/8", iconText: "text-white", iconBorder: "border-white/15",
                      glow: "bg-[#0055FF]",
                      chipBg: "bg-white/10", chipText: "text-white/80",
                      base: "bg-slate-900", baseText: "text-white", baseBorder: "border-slate-800",
                    }
                  : isSilver
                  ? {
                      bg: "bg-white",
                      border: "border-slate-300",
                      text: "text-slate-900",
                      subtext: "text-slate-500",
                      divider: "border-slate-200",
                      iconBg: "bg-slate-100", iconText: "text-slate-700", iconBorder: "border-slate-200",
                      glow: "bg-slate-300",
                      chipBg: "bg-slate-100", chipText: "text-slate-600",
                      base: "bg-slate-100", baseText: "text-slate-700", baseBorder: "border-slate-200",
                    }
                  : {
                      bg: "bg-white",
                      border: "border-slate-200",
                      text: "text-slate-900",
                      subtext: "text-slate-500",
                      divider: "border-slate-100",
                      iconBg: "bg-slate-50", iconText: "text-slate-600", iconBorder: "border-slate-200",
                      glow: "bg-slate-200",
                      chipBg: "bg-slate-50", chipText: "text-slate-500",
                      base: "bg-slate-50", baseText: "text-slate-600", baseBorder: "border-slate-200",
                    };
                const Icon = isWinner ? Crown : Medal;
                const rankLabel = isWinner ? "Champion" : isSilver ? "Runner-up" : "Third";
                // Podium tier effect via top-margin (cards retain natural content height).
                // Champion sits highest (no offset); runner-up and third are pushed down.
                const offset = isWinner ? "" : isSilver ? "mt-4 sm:mt-8" : "mt-8 sm:mt-14";
                return <PodiumCard key={p.rank} partner={p} accent={accent} offset={offset} icon={Icon} rankLabel={rankLabel} />;
              })}
            </div>

            {/* Rest of leaderboard (rank 4-10) */}
            {rest.length > 0 && (
              <Card className="rounded-xl shadow-soft border-slate-200 overflow-hidden">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                        <th className="py-3 px-5 font-medium w-12">Rank</th>
                        <th className="font-medium">Partner</th>
                        <th className="font-medium hidden md:table-cell">Trend (14d)</th>
                        <th className="font-medium text-right hidden sm:table-cell">Signups</th>
                        <th className="font-medium text-right hidden sm:table-cell">Conv.</th>
                        <th className="font-medium text-right pr-5">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rest.map((p) => (
                        <tr key={p.rank} className="border-t border-slate-100 hover:bg-slate-50/40 transition-colors" data-testid={`row-rank-${p.rank}`}>
                          <td className="py-3.5 px-5 font-mono text-slate-500 tabular-nums">#{p.rank}</td>
                          <td className="py-3.5">
                            <div className="flex items-center gap-2.5">
                              <span className="text-lg">{flag(p.country)}</span>
                              <div className="leading-tight">
                                <div className="font-medium text-slate-900">{p.alias}</div>
                                {p.city && <div className="text-[11px] text-slate-500">{p.city}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 hidden md:table-cell"><Spark data={p.spark} color={p.growth_pct >= 0 ? "#10B981" : "#0055FF"} /></td>
                          <td className="py-3.5 text-right text-slate-700 font-mono tabular-nums hidden sm:table-cell">{p.signups.toLocaleString()}</td>
                          <td className="py-3.5 text-right text-slate-700 font-mono tabular-nums hidden sm:table-cell">{p.conversion_pct}%</td>
                          <td className="py-3.5 pr-5 text-right text-slate-900 font-mono font-semibold tabular-nums">{fmtGBP(p.commission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-[#0055FF]/10 text-[#0055FF] mb-5">
            <TrendingUp className="size-5" />
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl mx-auto">Your name could be on this board next month.</h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto text-[15px]">Join the Connexis partner programme — refer financial brands to our platform and earn recurring commission on every account they fund.</p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/partners">
              <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="leaderboard-cta-apply">
                Become a partner <ArrowUpRight className="size-4 ml-1" />
              </Button>
            </Link>
            <Link to="/partners#commission">
              <Button variant="outline" className="rounded-full" data-testid="leaderboard-cta-pricing">View commission structure</Button>
            </Link>
          </div>
          {data && (
            <div className="mt-10 text-[11px] uppercase tracking-widest text-slate-500">
              {data.total_partners.toLocaleString()} active partners · paid every Friday
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
